import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Clock, AlertTriangle, Star } from 'lucide-react';



const ReliabilityMetrics = ({ provider, outages = [] }) => {
  const [metrics, setMetrics] = useState({
    averageDowntime: 0,
    mttr: 0, // Mean Time To Recovery
    outageFrequency: 0,
    reliability: 0,
    userSatisfaction: 4.2 // Mock data
  });

  useEffect(() => {
    calculateMetrics();
  }, [outages]);

  const calculateMetrics = () => {
    if (!outages?.length) {
      setMetrics(prev => ({ ...prev, reliability: 99.9 }));
      return;
    }

    // Calculate average downtime (mock calculation based on outage count)
    const totalOutages = outages?.length;
    const avgDowntime = totalOutages * 2.5; // Mock: assume avg 2.5 hours per outage

    // Calculate MTTR (mock: varies by severity)
    const avgMTTR = outages?.reduce((acc, outage) => {
      const severityMultiplier = {
        low: 1,
        medium: 2,
        high: 4,
        critical: 8
      };
      return acc + (severityMultiplier?.[outage?.severity] || 2);
    }, 0) / totalOutages;

    // Calculate outage frequency (per month)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
    
    const recentOutages = outages?.filter(outage => 
      new Date(outage?.created_at) >= thirtyDaysAgo
    );

    // Calculate reliability percentage
    const reliability = Math.max(95, 100 - (totalOutages * 0.5));

    setMetrics({
      averageDowntime: avgDowntime,
      mttr: avgMTTR,
      outageFrequency: recentOutages?.length,
      reliability: reliability,
      userSatisfaction: 4.2 - (totalOutages * 0.1) // Mock calculation
    });
  };

  const MetricCard = ({ title, value, unit, icon: IconComponent, trend, description }) => (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <IconComponent className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        </div>
        {trend && (
          <div className={`flex items-center ${trend > 0 ? 'text-red-500' : 'text-green-500'}`}>
            {trend > 0 ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
          </div>
        )}
      </div>
      
      <div className="flex items-baseline">
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        {unit && <span className="text-sm text-gray-500 ml-1">{unit}</span>}
      </div>
      
      {description && (
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Reliability"
        value={metrics?.reliability?.toFixed(1)}
        unit="%"
        icon={TrendingUp}
        trend={metrics?.reliability > 99 ? -1 : 1}
        description="Uptime percentage"
      />
      
      <MetricCard
        title="Avg. Downtime"
        value={metrics?.averageDowntime?.toFixed(1)}
        unit="hrs/month"
        icon={Clock}
        trend={metrics?.averageDowntime > 5 ? 1 : -1}
        description="Average monthly downtime"
      />
      
      <MetricCard
        title="Recovery Time"
        value={metrics?.mttr?.toFixed(1)}
        unit="hrs"
        icon={AlertTriangle}
        trend={metrics?.mttr > 3 ? 1 : -1}
        description="Mean time to recovery"
      />
      
      <MetricCard
        title="User Rating"
        value={metrics?.userSatisfaction?.toFixed(1)}
        unit="/5.0"
        icon={Star}
        trend={metrics?.userSatisfaction > 4 ? -1 : 1}
        description="Average user satisfaction"
      />
    </div>
  );
};

export default ReliabilityMetrics;